/**
 * @Author: Anandp
 * @Date:   2024-12-13 13:08:22
 * @Last Modified by:   Anandp
 * @Last Modified time: 2024-12-23 15:46:17
 */
// Remove if react-draft-wysiwyg is not used
/* import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'; */
// Remove if simplebar is not used
import 'simplebar-react/dist/simplebar.min.css';
// Remove if mapbox is not used
import 'mapbox-gl/dist/mapbox-gl.css';
import { useState, useEffect } from 'react';
import { useLocation, useRoutes } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Provider as ReduxProvider } from 'react-redux';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
/* import { RTL } from 'src/components/rtl'; */
import { SplashScreen } from 'src/components/splash-screen';
import { SettingsButton } from 'src/components/settings/settings-button';
import { SettingsDrawer } from 'src/components/settings/settings-drawer';
// import { Toaster } from 'src/components/toaster';
import { SettingsConsumer, SettingsProvider } from 'src/contexts/settings-context';
import { AuthConsumer, AuthProvider } from 'src/contexts/auth/multi-auth-context';
import { gtmConfig } from 'src/config';
import { useNprogress } from 'src/hooks/use-nprogress';
import { useAnalytics } from 'src/hooks/use-analytics';
import { routes } from 'src/routes';
import { store } from 'src/store';
import { createTheme } from 'src/theme'; 
import LinkExpired from 'src/layouts/wiredUpSurveys/surveys/linkExpire';
import AccessDeniedLockScreen from 'src/components/access-denied-lock-screen';

// Remove if locales are not used
import 'src/locales/i18n';

export const App = () => {
  useAnalytics(gtmConfig);
  useNprogress(); 
  const element = useRoutes(routes);
  const location = useLocation();
  const [isAccessDenied, setIsAccessDenied] = useState(false);
  const [accessDeniedMessage, setAccessDeniedMessage] = useState('');

  // Automatically reset the access denied screen when navigating to a new route
  useEffect(() => {
    setIsAccessDenied(false);
    setAccessDeniedMessage('');
  }, [location.pathname, location.search]);

  // Listen to global 403 Forbidden events dispatched by Axios response interceptor
  useEffect(() => {
    const handleAccessDenied = (event) => {
      setIsAccessDenied(true);
      if (event.detail?.message) {
        setAccessDeniedMessage(event.detail.message);
      }
    };

    window.addEventListener('app-access-denied', handleAccessDenied);
    return () => {
      window.removeEventListener('app-access-denied', handleAccessDenied);
    };
  }, []);

  const publicRoutes = ['/','/login', '/forgot-password','/myMojoSurvey','/mtpsurvey'];
const isPublicRoute = publicRoutes.includes(location.pathname);
  return (
    <ReduxProvider store={store}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <AuthProvider>
          <AuthConsumer>
            {(auth) => {
                 // For public routes, bypass auth checks entirely
            const showSlashScreen = isPublicRoute ? false : !auth.isInitialized;
            
              return (
              <SettingsProvider>
                <SettingsConsumer>
                  {(settings) => {
                    // Prevent theme flicker when restoring custom settings from browser storage
                    if (!settings.isInitialized) {
                      // return null;
                    }

                    const theme = createTheme({
                      colorPreset: settings.colorPreset,
                      contrast: settings.contrast,
                      direction: settings.direction,
                      paletteMode: settings.paletteMode,
                      responsiveFontSizes: settings.responsiveFontSizes
                    });

                    // Prevent guards from redirecting
                    // const showSlashScreen = !auth.isInitialized;
                   
                    return (
                      <ThemeProvider theme={theme}>
                        <Helmet>
                          <meta
                            name="color-scheme"
                            content={settings.paletteMode}
                          />
                          <meta
                            name="theme-color"
                            content={theme.palette.neutral[900]}
                          />
                        </Helmet>
                       {/* <RTL direction={settings.direction}> */}
                          <CssBaseline />
                          {showSlashScreen ? (
                            <SplashScreen />
                          ) : auth.surveyExpired ? (
                            <LinkExpired />
                          ) : isAccessDenied ? (
                            <AccessDeniedLockScreen
                              message={accessDeniedMessage}
                              onBack={() => {
                                setIsAccessDenied(false);
                                if (window.history.length > 1) {
                                  window.history.back();
                                } else {
                                  window.location.href = '/teamDashboard';
                                }
                              }}
                            />
                          ) : (
                            <>
                              {element}
                              <SettingsButton onClick={settings.handleDrawerOpen} />
                              <SettingsDrawer
                                canReset={settings.isCustom}
                                onClose={settings.handleDrawerClose}
                                onReset={settings.handleReset}
                                onUpdate={settings.handleUpdate}
                                open={settings.openDrawer}
                                values={{
                                  colorPreset: settings.colorPreset,
                                  contrast: settings.contrast,
                                  direction: settings.direction,
                                  paletteMode: settings.paletteMode,
                                  responsiveFontSizes: settings.responsiveFontSizes,
                                  stretch: settings.stretch,
                                  layout: settings.layout,
                                  navColor: settings.navColor
                                }}
                              />
                            </>
                          )}
                          {/* <Toaster /> */}
                          <ToastContainer />
                        {/* </RTL> */}
                      </ThemeProvider>
                    );
                  }}
                </SettingsConsumer>
              </SettingsProvider>
            )}}            
          </AuthConsumer>
        </AuthProvider>
      </LocalizationProvider>
    </ReduxProvider>
  );
};
